// OrganizationDetails.tsx
import React, { useEffect, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    AppShell,
    Box,
    Button,
    Flex,
    Grid,
    ScrollArea,
    Space,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Group,
    Drawer, Select, Notification,
} from '@mantine/core';
import { Organization } from '../../../models/Organization';
import { StorageLocation } from '../../../models/StorageLocation';
import { MembershipRole } from '../../../models/Membership';

import { getToken } from '../../../auth/service/authService';
import { CreateStorageLocation } from '../../storageLocation/Admin/CreateStorageLocation';
import {fetchOrganizationById} from "../../../Services/OrganizationService";
import {
    createStorageLocation,
    deleteStorageLocation,
    fetchStorageLocationsByOrgId
} from "../../../Services/StorageLocationService";
import {inviteMemberToOrganization} from "../../../Services/InvitationService";
import {IconCheck, IconX} from "@tabler/icons-react";
import {promoteMembership} from "../../../Services/MembershipService";

const OrganizationDetails: React.FC = () => {
    const { id } = useParams(); // orgId from the URL
    const navigate = useNavigate();
    const orgId = String(id);
    const token = getToken();

    // Organization
    const [organization, setOrganization] = useState<Organization | null>(null);

    // Storage Locations
    const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);

    // Drawers
    const [inviteDrawerOpened, setInviteDrawerOpened] = useState(false);
    const [locationDrawerOpened, setLocationDrawerOpened] = useState(false);

    // Invite form fields
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<MembershipRole>(MembershipRole.MEMBER);

    // Create location form fields
    const [locationName, setLocationName] = useState('');
    const [locationDesc, setLocationDesc] = useState('');

    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);


    useEffect(() => {
        if (!token) {
            console.error('No token found.');
            return;
        }
        if (Number.isNaN(orgId)) {
            console.error('Invalid organization ID');
            return;
        }

        loadOrganization();
        loadStorageLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const loadOrganization = async () => {
        try {
            const orgData = await fetchOrganizationById(orgId);
            setOrganization(orgData);
        } catch (error) {
            console.error('Error fetching organization details:', error);
        }
    };

    const loadStorageLocations = async () => {
        try {
            const locations = await fetchStorageLocationsByOrgId(orgId);
            setStorageLocations(locations);
        } catch (error) {
            console.error('Error fetching storage locations:', error);
        }
    };

    // Invite a new member
    const handleInviteSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!token || !organization) return;

        try {
            const response = await inviteMemberToOrganization(organization.id, inviteEmail, inviteRole);
            console.log(response)
            // close drawer, reset
            setInviteEmail('');
            setInviteRole(MembershipRole.MEMBER);
            // On success, the server returns the created invitation
            setSuccessMessage(`Invitation sent to ${inviteEmail}. Token: ${response.data.token}`);
        } catch (err: any) {
            console.log('Error inviting member:', err);
            setErrorMessage(err.response.data)
        }
    };

    // Create a new storage location
    const handleCreateLocation = async (e: FormEvent) => {
        e.preventDefault();
        if (!token) return;

        try {
            await createStorageLocation(orgId, locationName, locationDesc);
            // Refresh
            loadStorageLocations();
            setLocationDrawerOpened(false);
            setLocationName('');
            setLocationDesc('');
        } catch (err) {
            console.error('Error creating storage location:', err);
        }
    };

    const handleDeleteStorageLocation = async (storageLocationId : string) => {
        try {
            await deleteStorageLocation(storageLocationId);
            // Refresh
            loadStorageLocations();
        } catch (err) {
            console.error('Error creating storage location:', err);
        }
    }

    const handlePromoteMembeship = async (membershipId : string) => {
        try {
            await promoteMembership(membershipId);
            // Refresh
            loadOrganization();
        } catch (err) {
            console.error('Error creating storage location:', err);
        }
    }

    if (!organization) {
        return (
            <Box>
                <Title>Betrieb nicht gefunden</Title>
                <Button onClick={() => navigate(-1)}>Zurück</Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Top info / header area */}
            <AppShell.Section>
                <Flex align="center" gap="sm">
                    <Button variant="filled" onClick={() => navigate(-1)}>
                        Zurück
                    </Button>
                    <Title order={2}>Bearbeite Betrieb: {organization.name}</Title>
                </Flex>
                <Space h="md" />
                <Text>
                    {organization.street}, {organization.city}, {organization.state} {organization.zip},{' '}
                    {organization.country}
                </Text>
                <Space h="md" />
            </AppShell.Section>

            {/* Main content: two columns (Memberships / Storage Locations) */}
            <Grid gutter="md" grow style={{ minHeight: '60vh' }}>
                {/* MEMBERSHIPS COLUMN */}
                <Grid.Col
                    span={{ xs: 12, md: 6 }}
                    style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                    <Flex align="center" justify="space-between">
                        <Title order={3}>Mitgliedschaften:</Title>
                        <Button variant="filled" onClick={() => setInviteDrawerOpened(true)}>
                            Lade Mitglied ein
                        </Button>
                    </Flex>
                    <Space h="md" />

                    {/* Scrollable table */}
                    <ScrollArea style={{ flex: 1 }}>
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Nutzername</Table.Th>
                                    <Table.Th>Rolle</Table.Th>
                                    <Table.Th></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            {organization.memberships.length === 0 && (
                                <Text>Keine Mitgliedschaften bisher</Text>
                            )}
                            <Table.Tbody>
                                {organization.memberships.map((mKey) => (
                                    <Table.Tr key={mKey.id}>
                                        <Table.Td>{mKey.userKey.username}</Table.Td>
                                        <Table.Td>{mKey.membershipRole}</Table.Td>
                                        <Table.Td>
                                            {
                                                mKey.membershipRole === MembershipRole.MEMBER
                                                ? <Button variant={"filled"} onClick={() => handlePromoteMembeship(mKey.id)}>Befördern</Button>
                                                : <></>
                                            }
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Grid.Col>

                {/* STORAGE LOCATIONS COLUMN */}
                <Grid.Col
                    span={{ xs: 12, md: 6 }}
                    style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                    <Flex align="center" justify="space-between">
                        <Title order={3}>Stellplätze</Title>
                        <CreateStorageLocation organizationId={organization.id} onCreated={loadStorageLocations} />
                    </Flex>
                    <Space h="md" />

                    {/* Scrollable table */}
                    <ScrollArea style={{ flex: 1 }}>
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Name</Table.Th>
                                    <Table.Th>Beschreibung</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {storageLocations.map((loc) => (
                                    <Table.Tr key={loc.id}>
                                        <Table.Td>{loc.name}</Table.Td>
                                        <Table.Td>{loc.description}</Table.Td>
                                        <Table.Td><Button variant={"filled"} onClick={() => handleDeleteStorageLocation(loc.id)}>Löschen</Button></Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Grid.Col>
            </Grid>

            {/* Drawer for inviting a new member */}
            <Drawer
                opened={inviteDrawerOpened}
                onClose={() => setInviteDrawerOpened(false)}
                title="Lade neues Mitglied ein"
                padding="xl"
                size="xl"
            >
                {errorMessage && (
                    <Notification
                        icon={<IconX size={18} />}
                        color="red"
                        mb="md"
                        onClose={() => setErrorMessage(null)}
                    >
                        {errorMessage}
                    </Notification>
                )}
                {/* Display success or error */}
                {successMessage && (
                    <Notification
                        icon={<IconCheck size={18} />}
                        color="teal"
                        mb="md"
                        onClose={() => setSuccessMessage(null)}
                    >
                        {successMessage}
                    </Notification>
                )}
                <form onSubmit={handleInviteSubmit}>
                    <Stack>
                        <TextInput
                            label="E-Mail"
                            placeholder="mitarbeiter@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.currentTarget.value)}
                            required
                        />
                        <Select
                            label="Rolle"
                            placeholder="Wähle eine Rolle aus"
                            data={[
                                { value: MembershipRole.MEMBER, label: "Mitglied" },
                                { value: MembershipRole.ADMIN, label: "Administrator" },
                            ]}
                            value={inviteRole}
                            onChange={(value) => setInviteRole(value as MembershipRole)}
                            required
                        />
                        <Group p="right" mt="md">
                            <Button variant="subtle" onClick={() => setInviteDrawerOpened(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit">Sende Einladung</Button>
                        </Group>
                    </Stack>
                </form>
            </Drawer>

            {/* Drawer for creating a new storage location */}
            <Drawer
                opened={locationDrawerOpened}
                onClose={() => setLocationDrawerOpened(false)}
                title="Create Storage Location"
                padding="xl"
                size="xl"
            >
                <form onSubmit={handleCreateLocation}>
                    <Stack>
                        <TextInput
                            label="Name"
                            placeholder="Main Warehouse"
                            value={locationName}
                            onChange={(e) => setLocationName(e.currentTarget.value)}
                            required
                        />
                        <TextInput
                            label="Beschreibung"
                            placeholder="An optional description"
                            value={locationDesc}
                            onChange={(e) => setLocationDesc(e.currentTarget.value)}
                        />
                        <Group p="right" mt="md">
                            <Button variant="subtle" onClick={() => setLocationDrawerOpened(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit">Erstellen</Button>
                        </Group>
                    </Stack>
                </form>
            </Drawer>
        </Box>
    );
};

export default OrganizationDetails;
